import AmpService, { AMP_CATEGORY_LOCATIONS } from "analytics/amplitude/amplitudeEvents";
import DepartmentMenu from "component/department-menu/DepartmentMenu";
import Element from "component/design-system/react-scroll/components/Element";
import PAText, { FONT_SIZE } from "component/design-system/typography/PAText/PAText";
import ArticleGuide from "component/guide-articles/ArticleGuide";
import styles from "modules/home/home.module.scss";
import { useTranslation } from "next-i18next";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import { useState } from "react";
import HideIf from "util/HideIf";
import isHighTrafficPage from "util/helper/isHighTrafficPage";
import isHomePage from "util/helper/isHomePage";
import { scrollTo } from "../../util/scroll-helper";
import FeaturedMakeAndManufacture from "./FeaturedMakeAndManufacture";
import TabsCategory from "./TabsCategory";
import AvatarClub from "./avatar-club/AvatarClub";
import HomeBnpl from "./bnpl/HomeBnpl";
import CarPartsContent from "./car-parts/CarPartsContent";
import { CardData } from "./constant";
import Heading from "./headings/Headings";
import HomeBrandsSectionContainer from "./home-brands-section/HomeBrandsSectionContainer";
import MerchandiseSlider from "./merchandise-slider/MerchandiseSlider";
import PerkCard from "./perk-card/PerkCard";
import RegularService from "./regular-service/RegularService";
import ShopWheelTire from "./shop-wheel-tire/ShopWheelTire";
import { tabIndexMap } from "./tabIndex";

const CustomerReviewV2 = dynamic(() => import(/* webpackChunkName: "customer-review-v2" */ "component/customer-review/CustomerReviewV2"));
const ProductRecentlyViewed = dynamic(() => import(/* webpackChunkName: "recently-viewed-loader" */ "component/product/recent-viewed-products/ProductRecentlyViewed"), { ssr: false });
const HomeVideoSectionContainer = dynamic(() => import(/* webpackChunkName: "home-video-section" */ "./home-video-section/HomeVideoSectionContainer"));
const HomeRecommendationCards = dynamic(() => import(/* webpackChunkName: "home-recommendation-cards" */"./home-recommendation-cards/HomeRecommendationCards"));

const scrollSettings = {
  duration: 300,
  delay: 0,
  smooth: true,
  offset: -75,
};

function HomeRegularContainer(props: any) {
  const defaultTab = props.topCategories?.length > 0 ? 1 : 2;
  const [categoryActiveTab, setCategoryActiveTab] = useState(defaultTab);
  const {
    sponsoredBrandsV2,
    googleReviews,
    articles,
    topCategories,
    regularServices,
    highTrafficPageData,
    // categoryCards,
  } = props;

  const router = useRouter();
  const { pathname } = router;

  const { t } = useTranslation("home");

  function handleSelectDepartmentItem(_department: any) {
    setCategoryActiveTab(_department.tabIndex);
    setTimeout(() => {
      scrollTo("anchor-tab-category", scrollSettings);
    });
  }

  return (
    <>

      <div
        className={styles.section}
        style={{
          marginBottom: "calc(3 * var(--safari-root-font, 10px))", backgroundColor: "transparent", marginTop: 0,
        }}
      >
        <PAText component="h1" className="m-t-0" bold centerAlign>{highTrafficPageData?.h1 || t("auto_parts_in_canada")}</PAText>
        <Heading
          heading={highTrafficPageData?.Tier0s.H2 || t("oem_catalog")}
          component="h2"
          fontSize={FONT_SIZE[2]}
          className="m-t-0"
          color="light-grey"
          bold={false}
        />

        <CarPartsContent
          content={highTrafficPageData?.Tier0s.content || t("oem_description")}
          backgroundColor="transparent"
        />
        <DepartmentMenu onSelectItem={handleSelectDepartmentItem} />
      </div>

      <HomeRecommendationCards />

      {/* <HideIf show={!isHighTrafficPage(router)}>
        <HomepageCategory categoryCards={categoryCards} />
      </HideIf> */}

      <HideIf show={isHighTrafficPage(router)}>
        <div className="m-b-30">
          <FeaturedMakeAndManufacture
            content={highTrafficPageData?.Makes_Models.content}
            heading={highTrafficPageData?.Makes_Models.H2}
          />
        </div>
      </HideIf>

      <HideIf show={(highTrafficPageData?.All_Categories.H2 && highTrafficPageData?.All_Categories.content) || isHomePage(pathname)}>

        <CarPartsContent
          heading={highTrafficPageData?.All_Categories.H2 || t("shop_all_auto_parts")}
          content={highTrafficPageData?.All_Categories.content || t("auto_parts_description")}
          backgroundColor="transparent"
        />

        <Element name="anchor-tab-category">
          <div className="m-t-30">
            <TabsCategory
              topCategories={topCategories}
              currentTab={categoryActiveTab}
              onChange={(index: number) => {
                if (tabIndexMap[index]) {
                  AmpService.categoryClick({ tier0: tabIndexMap[index] }, AMP_CATEGORY_LOCATIONS.MAIN_CATEGORY_GRID);
                }
                setCategoryActiveTab(index);
              }}
              component="h3"
            />
          </div>
        </Element>
      </HideIf>

      <HideIf show={!isHighTrafficPage(router)}>
        <RegularService regularServices={regularServices} />
        <AvatarClub />
        <ShopWheelTire />
        <div className={`d-flex justify-content-center ${styles.perkCard}`}>
          {CardData.map((each) => (
            <PerkCard {...each} key={each.title} />
          ))}
        </div>
        <HideIf show={!isHighTrafficPage(router)}>
          <div className="m-b-30">
            <FeaturedMakeAndManufacture
              content={highTrafficPageData?.Makes_Models.content}
              heading={highTrafficPageData?.Makes_Models.H2}
            />
          </div>
        </HideIf>

        <HideIf show={!isHighTrafficPage(router)}>
          <HomeBnpl />
        </HideIf>

        <div className={styles.full_width}>
          <CustomerReviewV2 reviews={googleReviews} />
        </div>

        <HideIf show={!isHighTrafficPage(router)}>
          <div className="m-b-30 m-t-40">
            <div className={`${styles.section}`}>
              <PAText
                bold
                component="h2"
                className="d-flex align-items-center justify-content-center m-b-15"
                style={{ width: "100%", marginTop: 0 }}
                fontSize={FONT_SIZE[5]}
              >
                {t("Shop Merchandise")}
              </PAText>
              <MerchandiseSlider />
            </div>
          </div>
        </HideIf>

        {articles && articles.length > 0 && (
          <ArticleGuide
            articles={articles}
            tileWidth={300}
            isDate
            showArrowOnHover
            showScrollOnHover
            grayBackground
          />
        )}
      </HideIf>
      <HomeBrandsSectionContainer highTrafficPageData={highTrafficPageData} sponsoredBrandsV2={sponsoredBrandsV2} />

      {/* FAQs */}
      <CarPartsContent
        contentIds={highTrafficPageData?.contentIds}
        backgroundColor="transparent"
      />
      <HomeVideoSectionContainer highTrafficPageData={highTrafficPageData} />

      <HideIf show={!isHighTrafficPage(router)}>
        <div className="m-b-30 m-t-30">
          <ProductRecentlyViewed />
        </div>
      </HideIf>
    </>
  );
}

export default HomeRegularContainer;
